import React from "react";
import { motion } from "framer-motion";
import { Ban, Gift, Eye, Heart } from "lucide-react";
import { GOOFBALL_IMG } from "./constants";

const RULES = [
    {
        icon: Gift,
        title: "Add value before asking.",
        body: "Our community thrives because members give first. Abundance, not extraction.",
    },
    {
        icon: Eye,
        title: "Show up honestly.",
        body: "Authentic representation of yourself and your business builds the trust every real relationship needs.",
    },
    {
        icon: Ban,
        title: "No spam. No hijacks. No fake pitches.",
        body: "We actively protect our members from typical networking annoyances with strict moderation and clear guidelines.",
    },
    {
        icon: Heart,
        title: "Respect others, always.",
        body: "Professional courtesy and genuine respect aren't negotiable here.",
    },
];

const CodeOfConduct = () => {
    return (
        <section
            id="code"
            data-testid="code-of-conduct"
            className="relative max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-36"
        >
            <div className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr] gap-10 md:gap-16 items-center mb-14 md:mb-20">
                <div className="max-w-3xl order-2 md:order-1">
                    <div className="text-xs uppercase tracking-[0.3em] text-[#1ce5da] font-display mb-4">
                        VII. The Code
                    </div>
                    <h2 className="font-display font-black tracking-tighter text-5xl md:text-7xl leading-[0.9] text-[#f6f1e1]">
                        This is a{" "}
                        <span className="font-serif-italic font-light text-[#f2c652]">
                            Goofball-free
                        </span>{" "}
                        zone.
                    </h2>
                    <p className="mt-6 text-[#f6f1e1]/60 font-body text-base md:text-lg max-w-2xl">
                        Every member earns the "Not A Goofball" badge after
                        completing our code of conduct training. Everyone here
                        understands how we treat each other.
                    </p>
                </div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.85, rotate: -8 }}
                    whileInView={{ opacity: 1, scale: 1, rotate: -4 }}
                    whileHover={{ rotate: 0, scale: 1.04 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    className="relative order-1 md:order-2 mx-auto w-full max-w-[360px]"
                    data-testid="goofball-mascot-wrap"
                >
                    {/* Cyan halo */}
                    <div
                        className="absolute inset-0 rounded-full blur-3xl opacity-60"
                        style={{
                            background:
                                "radial-gradient(circle, rgba(239,68,68,0.35) 0%, rgba(28,229,218,0.15) 45%, transparent 70%)",
                        }}
                        aria-hidden="true"
                    />
                    <img
                        src={GOOFBALL_IMG}
                        alt="Don't be a Goofball. Deeper Networking"
                        className="relative w-full h-auto select-none"
                        draggable="false"
                        data-testid="goofball-mascot-img"
                    />
                </motion.div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                {RULES.map((r, i) => {
                    const Icon = r.icon;
                    return (
                        <motion.div
                            key={r.title}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: i * 0.07 }}
                            className="group relative border border-[#d4af37]/15 bg-[#0e1130] p-7 md:p-8 hover:border-[#f2c652]/50 hover:bg-[#141838] transition-all"
                            data-testid={`rule-${i + 1}`}
                        >
                            <div className="flex items-start gap-5">
                                <div className="w-12 h-12 rounded-full border border-[#d4af37]/30 bg-[#d4af37]/5 flex items-center justify-center shrink-0 group-hover:border-[#f2c652] group-hover:bg-[#f2c652]/10 transition-colors">
                                    <Icon
                                        className="w-5 h-5 text-[#d4af37] group-hover:text-[#f2c652] transition-colors"
                                        strokeWidth={1.5}
                                    />
                                </div>
                                <div>
                                    <h3 className="font-display font-bold text-xl md:text-2xl text-[#f6f1e1] tracking-tight mb-2 leading-tight">
                                        {r.title}
                                    </h3>
                                    <p className="text-[#f6f1e1]/55 font-body text-[15px] leading-relaxed">
                                        {r.body}
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    );
                })}
            </div>

            <div
                className="mt-10 md:mt-14 relative overflow-hidden border-2 border-[#f2c652] bg-gradient-to-r from-[#d4af37]/10 via-[#f2c652]/5 to-[#1ce5da]/10 p-6 md:p-8 flex items-center justify-between gap-6 flex-col md:flex-row text-center md:text-left"
                data-testid="violators-banner"
            >
                <div>
                    <div className="font-display text-[11px] uppercase tracking-[0.35em] text-[#f2c652] mb-2">
                        Zero Tolerance
                    </div>
                    <div className="font-display font-black text-[#f6f1e1] text-2xl md:text-3xl tracking-tight">
                        All violators will be shown the door.
                    </div>
                </div>
                <span className="font-display text-xs uppercase tracking-[0.3em] text-[#f6f1e1]/80 border border-[#d4af37]/50 px-3 py-1.5 rounded-sm">
                    Zero Tolerance · No Exceptions
                </span>
            </div>
        </section>
    );
};

export default CodeOfConduct;
